import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { FacilityIdSchema } from "./facility.contract.js";
/**
 * Manual READ contract (MV1 — the first slice of the Manuals module). Lists the manuals attached to a facility (via its
 * `facility_category` joins) so the web UI can render a card grid when a facility is selected. READ ONLY — authoring
 * (create/edit/state transitions) and the content body (sections/steps → ADR-002) are deferred.
 *
 * Only the M1 SCALARS exist today (ADR-010) — no step/section count, no thumbnail image, no rating/permissions. The
 * card is built from what's real; deferred fields are simply absent from the schema (added when their slice lands).
 * Browser-safe: zod + ts-rest only (no ORM/Nest).
 */
const c = initContract();
/**
 * The manual lifecycle state (pg enum `manual_state`, ADR-010 §2). Stored as the *current* state only — the state
 * MACHINE (transitions + history) is a later ADR. Drives the card's state badge.
 */
export const MANUAL_STATES = ["draft", "ready_for_review", "confirmed", "marked"];
export const ManualStateSchema = z.enum(MANUAL_STATES);
/**
 * One manual as sent to the client — the M1 scalars + its (single, in-facility) category label. `realm` is implicit
 * (the caller's tenant). Read is active-only, so there's no `archived` flag (a soft-archived / soft-deleted manual is
 * never returned). Deferred (absent until their slice): step count (ADR-002), image (assets), rating/permissions.
 */
export const ManualSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    description: z.string().nullable(),
    state: ManualStateSchema,
    /** Where the manual is usable (ADR-057). At least one is on. `isArManual` means AR only. */
    is2dWebManual: z.boolean(),
    is3dWebManual: z.boolean(),
    isVrManual: z.boolean(),
    isArManual: z.boolean(),
    isPlaced: z.boolean(),
    isSensorManual: z.boolean(),
    handspeedCheck: z.boolean(),
    creatorUsername: z.string().nullable(),
    /** The comment attached when the manual was flagged (state `marked`) — a tooltip on the Marked badge. */
    markedComment: z.string().nullable(),
    /** The SOURCE manual id if this is a LINKED copy (ADR-020) — `null` for a normal/standalone manual. Drives the
     *  "Linked" (legacy Verknüpfung) label on the card + the drawer's create-link gate (a link can't be linked again). */
    parentManualId: z.number().int().nullable(),
    /**
     * Is someone editing this manual right now (ADR-026)? Drives the padlock on the card/row so an author sees it
     * BEFORE opening the editor, rather than being refused on entry.
     *
     * A boolean and nothing more — the holder's **identity is deliberately absent**, because this DTO is returned to
     * everyone who can view the manual and a viewer has no part in the locking protocol. "Being edited by {name}" comes
     * from the gated `getManualLock`, which refuses non-editors.
     */
    locked: z.boolean(),
    /** The parent `facility_category` join id (the manual's link to this facility + category). */
    facilityCategoryId: z.number().int(),
    /** The category the manual belongs to WITHIN this facility (each manual has exactly one) + its label. */
    categoryId: z.number().int(),
    categoryName: z.string(),
    /** Audit facts (ADR-015 Layer A). `createdAt`/`updatedAt` are ISO-8601 UTC strings. `lastUpdatedByUsername` is the
     *  last editor's name (the denormalized snapshot, else the FK-resolved `user.name`; null when unknown — e.g. a
     *  migrated manual whose editor wasn't captured). `creatorUsername` (above) is the creator. */
    createdAt: z.string(),
    updatedAt: z.string(),
    lastUpdatedByUsername: z.string().nullable(),
    /** The `manual.version` column (legacy parity — the manual table carried a version). The legacy optimistic-lock counter
     *  (the value at migration; `1` for manuals created in the rewrite). Lifecycle/state mutations use the state CAS +
     *  advisory lock for concurrency, NOT this column. NOTE (ADR-002 Phase 9): manual-BODY authoring commands now DO tick it
     *  — it is the optimistic-concurrency token for body edits (each command CASes `expectedVersion`) and, per ADR-021,
     *  re-opens rating after a published→draft→edit→re-publish. Surfaced in the details drawer as "Version N". */
    version: z.number().int(),
    /** The manual card image (FS Phase 4, ADR-017) — a short-lived **presigned GET** for the manual's CATEGORY icon (the
     *  RAW file, a small transparent-PNG glyph — the legacy thumbnailer flattens these to a blank default), else the
     *  realm's standard category icon, else `null`. The legacy renders this on the manual tile. The bucket is private, so
     *  the URL expires; `null` also when storage is unconfigured (the card shows a placeholder). */
    categoryImageUrl: z.string().url().nullable(),
});
export const ManualListSchema = z.object({ manuals: z.array(ManualSchema) });
/** A manual in a facility's trash (ADR-056): the card fields, when it was trashed and when it will be purged. `archived`: it
 *  returns to the archive on restore. `parentTrashed`: a linked copy whose source is in the trash too, so it cannot be
 *  restored yet. A flag, not the source's name: the source may sit in a facility the caller cannot see. */
export const TrashedManualSchema = ManualSchema.extend({ deletedAt: z.string(), purgeAt: z.string(), archived: z.boolean(), parentTrashed: z.boolean() });
export const ManualTrashSchema = z.object({ retentionDays: z.number().int(), manuals: z.array(TrashedManualSchema) });
/**
 * A manual SEARCH result (FN4b-2, the legacy "Suche"). The manual's card fields MINUS the full `description` (the raw
 * HTML body isn't shipped on a results list — a plain-text `snippet` stands in) and MINUS `version` (a details-drawer
 * concern the search card doesn't render — the search projections don't select it), PLUS its facility location. Permission-
 * filtered server-side to the caller's DIRECTLY-viewable facilities (the #107 predicate); active-only.
 */
export const ManualSearchResultSchema = ManualSchema.omit({ description: true, version: true, is2dWebManual: true, is3dWebManual: true, isVrManual: true }).extend({
    /** The manual's parent facility — "open" navigates to THAT facility's manuals view (`arc_facility`). */
    facilityId: z.number().int(),
    /** The facility's ltree path (ids) + leaf name — the web renders the ancestor breadcrumb (from its loaded tree by id). */
    facilityPath: z.string(),
    facilityName: z.string(),
    /** The (sanitized) description as PLAIN TEXT — tags stripped, HTML entities decoded, whitespace collapsed, capped. The
     *  card truncates it (expand to see the whole) and highlights the match. `null` when the manual has no description.
     *  Never raw HTML. */
    descriptionText: z.string().nullable(),
});
export const ManualSearchListSchema = z.object({ results: z.array(ManualSearchResultSchema) });
/** A linked-manual card (ADR-020) — the search-result card scalars + facility path + an `archived` flag (a link target
 *  can be archived while the link survives, so the web navigates to the matching Active/Archived filter — panel #3) +
 *  `canManage` = does the CALLER hold `manuals_manage` on THIS card's OWN facility (a child lives elsewhere than the
 *  source being viewed) → gates the unlink × per-card, not by the source facility's permission (panel). */
export const ManualLinkCardSchema = ManualSearchResultSchema.extend({ archived: z.boolean(), canManage: z.boolean() });
/** GET /manuals/:id/links → the `parent` (present when this manual is a linked child) + the `children` (present when it
 *  is a parent). Each card is filtered by the caller's view permission on ITS facility (#107 — a link never leaks a
 *  manual the caller can't see). */
export const ManualLinksSchema = z.object({
    parent: ManualLinkCardSchema.nullable(),
    children: z.array(ManualLinkCardSchema),
});
/** A manual id path param, bounded to Postgres int4 (see FacilityIdSchema for the rationale). */
export const ManualIdSchema = z.coerce.number().int().positive().max(2147483647);
/**
 * The "what you're deleting" snapshot for the hard-delete confirmation (ADR-012). `expected` is an opaque token
 * (`id : archived-flag : state : facility_category_id : name`) bound into the delete: it recomputes it under the
 * facility topology lock and 409s if a concurrent rename/move/archive changed it, so you never delete a manual other
 * than the one the dialog showed.
 */
export const ManualStatsSchema = z.object({
    /** The copies a move to the trash takes along (ADR-056); `linkedChildCount` is what a hard delete removes, trashed ones too. */
    liveLinkedChildCount: z.number().int(),
    name: z.string(),
    state: ManualStateSchema,
    categoryName: z.string(),
    expected: z.string(),
    /** Linked children (ADR-020) this delete will CASCADE — the confirmation warns "also deletes N linked copies in other
     *  facilities". 0 when the manual is not a linked parent. Bound into `expected` so it can't go stale. */
    linkedChildCount: z.number().int(),
});
/** A reviewer assigned to a manual (ADR-019) — the LOCAL user id + the display name (from the migrated `user` table). */
export const ManualAssignedUserSchema = z.object({
    userId: z.number().int(),
    name: z.string(),
    /** Human name parts (from the token; usually null for migrated users) — used for the avatar's First/Last initials;
     *  the display falls back to `name` (the username/email) when absent. */
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
});
export const ManualAssignedUsersSchema = z.object({ users: z.array(ManualAssignedUserSchema) });
/** The body for assign/unassign (ADR-022 Phase 2) — the target reviewer's LOCAL user id. */
export const ManualAssignBodySchema = z.object({ userId: z.number().int().positive() });
/** One manual audit-timeline event (ADR-015 Phase B) — a SAFE projection of `manual_audit`: the action, the resolved
 *  actor NAME (the snapshot at action time; null for legacy/unknown), the timestamp, and the mark reason when present.
 *  NEVER carries the forensic `actor_sub`/`actor_roles` (3-layer rule — those stay server-side). */
export const ManualHistoryActionSchema = z.enum([
    "create", "rename", "move", "duplicate", "archive", "unarchive", "delete",
    "submit_review", "return_to_draft", "confirm", "mark", "unmark",
    // Reviewer assignment (ADR-022) — these audit rows now flow through the timeline projection; the enum MUST include them
    // or a manual with assign/unassign history yields an out-of-enum action the client drops (panel High).
    "assign", "unassign",
    // Manual-body authoring (ADR-002 Phase 9) — every fine-grained outline/step edit writes ONE `edit_body` row (the verb
    // lives in the forensic `new_value.op`, not the action); include it or a body-edited manual drops the timeline event.
    "edit_body",
    // Edit-locking (ADR-026 Phase 1) — a confirm-tier force-release of another session's lock is audited. Same omission
    // class as assign/unassign/edit_body above: `getManualHistory` selects `action` VERBATIM with no server-side
    // whitelist, so leaving it out makes the timeline of any force-unlocked manual fail the client Zod parse and drop
    // ENTIRELY — not just that row (diff-panel F2).
    "force_unlock",
    // The trash (ADR-056). Same omission class: an action missing here drops the manual's whole timeline.
    "trash", "restore",
]);
export const ManualHistoryEventSchema = z.object({
    id: z.number().int(),
    action: ManualHistoryActionSchema,
    actorName: z.string().nullable(),
    createdAt: z.string(), // ISO-8601 UTC
    comment: z.string().nullable(), // the mark reason (from new_value.comment); null for every other action
});
export const ManualHistorySchema = z.object({ events: z.array(ManualHistoryEventSchema) });
/** A `:ratingId` path param — a positive int32 (like {@link ManualIdSchema}). */
export const RatingIdSchema = z.coerce.number().int().positive().max(2147483647);
/** One rating on a manual (ADR-021). `userName` is the resolved rater name (like history — NEVER `sub`/roles);
 *  `comment` is server-sanitized plain text (render as a TEXT node, never HTML). */
export const RatingSchema = z.object({
    id: z.number().int(),
    rating: z.number().int().min(1).max(5),
    comment: z.string().nullable(),
    userName: z.string().nullable(),
    checked: z.boolean(),
    manualVersion: z.number().int(),
    createdAt: z.string(), // ISO-8601 UTC
});
/** The per-manual ratings summary (ADR-021 read): the cross-version aggregate + the caller's OWN row + the identified
 *  list (shipped to any DIRECT-view caller — the owner-decided divergence from legacy's confirm-only list). */
export const ManualRatingsSchema = z.object({
    average: z.number().nullable(), // null when count = 0
    count: z.number().int(),
    own: RatingSchema.nullable(),
    ratings: z.array(RatingSchema),
});
/** Body for PUT /manuals/:id/rating — upsert MY rating (1–5 + optional plain-text comment). */
export const UpsertRatingSchema = z.object({
    rating: z.number().int().min(1).max(5),
    comment: z.string().max(2000).nullish(),
});
/** Body for POST /manuals/:id/ratings/:ratingId/check — the confirm-tier moderation "seen" flag. */
export const CheckRatingSchema = z.object({ checked: z.boolean() });
/** The manual body — the normalized outline for the runtime viewer (ADR-002). One of the five legacy step types. */
export const ManualStepTypeSchema = z.enum(["regular", "question", "checklist", "question_with_score", "show_score"]);
/** A step's media (ADR-002 Phase 6) — an attached image/video on the main element, with a short-lived presigned GET
 *  (`url`, null when storage is unconfigured). `kind` is derived from the file extension; the viewer renders the
 *  matching `<img>`/`<video>`. */
export const ManualMediaRefSchema = z.object({
    fileId: z.number().int(),
    kind: z.enum(["image", "video", "model3d", "audio", "other"]),
    name: z.string().nullable(),
    url: z.string().url().nullable(),
});
/** The interactive payload for a typed step (ADR-002 Phase 6b), discriminated by `kind` (which matches the step `type`).
 *  Built server-side from the main element's `Question`/`Checklist`/`QuestionWithScore`/`ScoreLevel` settings (defensively
 *  parsed — migrated JSON may be dirty; a null payload means "couldn't build one"). Payload strings are PLAIN text (render
 *  as text nodes); the step's `text`/`completedText`/`failedText` are sanitized HTML. */
export const StepQuestionPayloadSchema = z.object({
    kind: z.literal("question"),
    question: z.string(),
    answers: z.array(z.string()),
    correctIndex: z.number().int().nullable(),
});
export const StepChecklistPayloadSchema = z.object({
    kind: z.literal("checklist"),
    text: z.string().nullable(),
    items: z.array(z.string()),
});
export const StepQuestionWithScorePayloadSchema = z.object({
    kind: z.literal("question_with_score"),
    question: z.string(),
    // `gotoAfterSectionName` = the legacy per-answer branch-routing target (a section NAME). Carried on the wire so an edit
    // round-trips it BY ANSWER IDENTITY (it moves with its answer object through reorder/delete), never re-bound by array
    // index (panel diff-9b-r2 Critical). `null` = no branch. The editor preserves it even without dedicated UI.
    answers: z.array(z.object({ text: z.string(), score: z.number().int(), correct: z.boolean(), gotoAfterSectionName: z.string().nullable() })),
});
export const StepShowScorePayloadSchema = z.object({
    kind: z.literal("show_score"),
    scoreLevel: z.number().int(),
});
export const StepPayloadSchema = z.discriminatedUnion("kind", [
    StepQuestionPayloadSchema,
    StepChecklistPayloadSchema,
    StepQuestionWithScorePayloadSchema,
    StepShowScorePayloadSchema,
]);
/**
 * A 3D vector, `[x, y, z]`.
 *
 * The DB stores geometry as verbatim `"x;y;z"` **varchars** — the Unity wire convention, kept byte-for-byte in the
 * shadow (`ar-element.md`). It is parsed SERVER-SIDE so that stringly-typed convention never reaches a client: every
 * consumer would otherwise re-implement `split(";")` and drift. Numbers, not strings, is also what
 * `ar-element.md`'s own rewrite note asks for.
 */
export const Vec3Schema = z.tuple([z.number(), z.number(), z.number()]);
/**
 * One AR element placed on a step (ADR-002 / AR editor).
 *
 * An AR manual's step is not just text: it carries a **main element** (the content carrier) and any number of extra
 * elements — arrows, hands, content panels. The legacy editor shows them as a tab strip and only loads them for an AR
 * manual (`ARLMS_SelectStep.js:91-100`); a document manual sees the main element alone. `elements` is therefore empty
 * for a non-AR manual.
 *
 * **Geometry is READ-ONLY here** (owner decision): 3D placement happens in the Unity canvas, and the web editor shows
 * the numbers Unity wrote. `SceneCenter` elements are excluded — that category is the manual-level settings carrier,
 * not a step element, and the legacy element picker hides it too.
 */
/**
 * The setting NAMES an author may see and (from ADR-027 Phase B) edit — the single source of truth for both the
 * server-side read filter and the client's renderers.
 *
 * Derived from the legacy's own dispatcher (`ARLMS_SettingUIs.js:1859`), which renders one container per setting row.
 * Everything ABSENT here is Unity-side plumbing — `Section`, `Step`, `SectionsAndSteps`, `TransformScene`,
 * `Interaction_*`, `RequiredInteractionHandler`, `ScoreLevel`, `dragtarget`, `Grabable`, `MrFish`,
 * `PersistXRElementAfterStep` — and must never reach the client: rendering it would expose the frozen Unity shadow as
 * if it were authored content (plan panel #6). The write endpoint uses this same list as its name allow-list, so a
 * client cannot PATCH shadow state (plan panel #5) — one list, two gates.
 */
export const AUTHOR_EDITABLE_SETTING_NAMES = [
    "Text",
    "Image",
    "Video",
    "AnimationState",
    "Path",
    "MeshObject",
    "MeshColor",
    "XRCModel",
    "XRCModelAndroid",
    "XRCModelIOS",
    "TrainerVideos",
    "RequiredInteraction",
    "LeftRightDirection",
    "AreaSize",
    "Goto",
    "ManageTraining",
    "Form",
    "Clickable",
    "BackgroundPanel",
    // The SEVEN sensor kinds (the legacy dispatches six; `PassivePlateSensorSetting` has no UI there at all). Since
    // ADR-037 the four the legacy has an editor for are WRITABLE; `Cable`/`Motor`/`PassivePlate` read but never write.
    "SwitchSensorSetting",
    "RemoteControlSensorSetting",
    "CableSensorSetting",
    "DualSwitchSensorSetting",
    "MotorSensorSetting",
    "MultiSwitchSensorSetting",
    "PassivePlateSensorSetting",
];
/** A file attached to a setting — `ar_element_setting_file`, NOT `setting_value`. */
export const ArElementSettingFileSchema = ManualMediaRefSchema.extend({
    /** The join row's own caption (`TrainerVideos` uses it to label each video). */
    description: z.string().nullable(),
});
/**
 * One setting on an AR element (ADR-027). The element's BODY is the list of these — the legacy renders one container
 * per row, dispatched on `name`, which is why there is no per-element-type layout anywhere in this contract.
 *
 * **`value` is not the whole story.** For the file-valued settings the identity lives in `files`, and `value` is
 * usually empty: measured on live rows, `XRCModel*` is 100% empty, `Video` 92%, `MeshObject` 81%, `Image` 68%. A
 * value-only shape would render every one of those as blank (plan panel #2).
 */
export const ArElementSettingSchema = z.object({
    /** The legacy dispatch key — `MeshColor`, `Path`, `RequiredInteraction`, … Always one of {@link AUTHOR_EDITABLE_SETTING_NAMES}. */
    name: z.string(),
    /** The raw stored scalar, verbatim. Null when the setting carries only files. */
    value: z.string().nullable(),
    /**
     * The value parsed as JSON when it is object/array-shaped, else null — parsed **tolerantly**, because the data is
     * not all valid JSON: 566 of 662 `Path` rows use UNQUOTED keys (`{ startPointElementId: 29354 }`) and would throw
     * `JSON.parse`. Same class as the malformed `SectionsAndSteps` the ETL already heals.
     *
     * Deliberately `unknown` for now: Phase A only DISPLAYS settings. Per-name typed variants land with their writers
     * in Phase B+, where each name gets a validator anyway.
     */
    json: z.unknown().nullable(),
    /** Attached files. A LIST — `TrainerVideos` carries several, so a single ref would lose all but one. */
    files: z.array(ArElementSettingFileSchema),
    /**
     * The bound sensor, for the `*SensorSetting` kinds only (ADR-037 D5). Carried HERE rather than fetched per element:
     * the MultiSwitch editor renders one row per `labels` entry, so a second request would be one per element, and the
     * sensors API is SuperAdmin-only while an author editing a manual is not.
     *
     * `null` when the setting binds no sensor, or the sensor was deleted — 11 production rows already carry a payload
     * with no sensor, and the editor states that plainly instead of failing.
     *
     * `labels` is INDEX-PRESERVING: entry `i` is channel `i`, and a non-string entry on the device comes through as
     * `null` rather than being dropped, because compacting would shift every later channel onto the wrong switch.
     */
    sensor: z
        .object({
        id: z.number().int(),
        displayName: z.string().nullable(),
        description: z.string().nullable(),
        labels: z.array(z.string().nullable()),
    })
        .nullable()
        .default(null),
});
/**
 * The write body for {@link setElementSetting}. `value` is the raw scalar the legacy stores; `fileIds` replaces the
 * setting's attachments wholesale (order preserved) for the file-valued settings.
 *
 * Both are optional and at least one must be present — a request that changes nothing is rejected rather than
 * silently bumping the manual version and busting a concurrent editor's CAS.
 */
export const SetElementSettingBodySchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    value: z.string().max(20_000).nullable().optional(),
    /** Replaces the attachment list wholesale. `[]` detaches everything. */
    fileIds: z.array(z.number().int()).max(50).optional(),
})
    .refine((b) => b.value !== undefined || b.fileIds !== undefined, { message: "value or fileIds is required" });
/** The setting cannot be written: not author-editable, sensor-backed, or the wrong shape for its name. */
export const SettingUnwritable = z.object({ message: z.string(), code: z.literal("SETTING_UNWRITABLE") });
export const ManualArElementSchema = z.object({
    id: z.number().int(),
    /** The technical/join key (e.g. `HandGreifen_v2`) — also how locale lookups and the legacy ordering arrays key. */
    name: z.string(),
    /** The human label (German in practice). Empty in the DB for many rows, where `name` is already the label. */
    displayName: z.string().nullable(),
    /** The AR-element category name (`MainElements`, `Arrows`, `Hands`, …) — behaviour, not decoration. */
    category: z.string().nullable(),
    blueprintId: z.number().int().nullable(),
    blueprintName: z.string().nullable(),
    /** This is the step's main element — the content carrier the step's text and media hang off. */
    isMain: z.boolean(),
    isPlaceable: z.boolean(),
    /** Unity has actually placed this in 3D (false on a fresh clone). */
    hasBeenPositioned: z.boolean(),
    /** Nesting — used only in xr-area today (35 rows); takeda has none. */
    parentId: z.number().int().nullable(),
    geometry: z.object({ position: Vec3Schema, rotation: Vec3Schema, size: Vec3Schema }),
    /** Presigned GET for the element's icon image, when it has one. */
    iconUrl: z.string().nullable(),
    /** The element's BODY (ADR-027) — one entry per author-editable setting; plumbing names are filtered server-side. */
    settings: z.array(ArElementSettingSchema),
});
/**
 * One blueprint tile in an element picker (the legacy `XR Element Auswahl` / `Hauptelement Auswahl` dialogs).
 *
 * A **blueprint** is an `ar_element` row with no `blueprint_ar_element_id` — a per-realm template that gets CLONED onto
 * a step. The catalogue is realm-specific by design: takeda and xr-area carry different categories and different
 * blueprint sets, which is why the picker is driven by data rather than a hard-coded list.
 */
export const ElementBlueprintSchema = z.object({
    id: z.number().int(),
    /** Technical key — the ordering + locale-lookup key, and what the legacy sorts by. */
    name: z.string(),
    /** Human label; falls back to `name`, which is already German where it is empty (`Pfad`, `Bild`, `Text`). */
    displayName: z.string().nullable(),
    category: z.string(),
    /** Placeable in 3D. The picker hides non-placeable tiles behind the "show non-placeable" switch. */
    isPlaceable: z.boolean(),
    /** Presigned GET for the tile image. Null ⇒ the client falls back to a category glyph. */
    iconUrl: z.string().nullable(),
    /** Hover/focus copy for the picker's description panel — entity-decoded and sanitized server-side. */
    description: z.string().nullable(),
    /**
     * The PHYSICAL DEVICE this tile places (ADR-038 D1). Set only for the sensor category, where the legacy's picker
     * offers one tile per device instead of one per blueprint; `null` everywhere else.
     *
     * `id` above stays the BLUEPRINT's, so several entries legitimately share it — the picker must key and track
     * selection on `sensor?.id ?? id`, never on `id` alone (plan panel, High).
     */
    sensor: z
        .object({
        id: z.number().int(),
        /** The device's own label, printed on the hardware — the legacy's corner `.idText`. */
        slug: z.string(),
        displayName: z.string().nullable(),
        description: z.string().nullable(),
    })
        .nullable()
        .default(null),
    /**
     * Why this entry cannot be placed, when that needs saying (ADR-038 D4). A registered device whose `type` matches no
     * placeable blueprint reaches the catalogue carrying this instead of being silently dropped, which is what the
     * legacy does (`console.error` and skip, with no empty state).
     */
    unavailableReason: z.string().nullable().default(null),
});
/** The realm's blueprint catalogue, grouped by category (categories alphabetical, blueprints by `name`). */
export const ElementBlueprintCatalogSchema = z.object({
    categories: z.array(z.object({ name: z.string(), blueprints: z.array(ElementBlueprintSchema) })),
});
/** A step in the viewer outline. `text`/`completedText`/`failedText` are the standard/completed/failed instruction states
 *  (sanitized HTML); `media` is the attached image/video; `payload` is the type-specific interactive content. */
export const ManualBodyStepSchema = z.object({
    id: z.number().int(),
    order: z.number().int(),
    name: z.string(),
    type: ManualStepTypeSchema,
    text: z.string().nullable(),
    completedText: z.string().nullable(),
    failedText: z.string().nullable(),
    media: ManualMediaRefSchema.nullable(),
    /** The completed / failed state's own image or video (ADR-027 §11 B) — the file attached to `Interaction_CompletedText`
     *  / `Interaction_FailedText`, the legacy's "Abgeschlossen Text und Bild/Video". Attached through the element-setting
     *  write with `fileIds` on that name (file only; the text is `editStepText`'s). */
    completedMedia: ManualMediaRefSchema.nullable(),
    failedMedia: ManualMediaRefSchema.nullable(),
    payload: StepPayloadSchema.nullable(),
    /** The step's AR elements, main first (ADR-002 / AR editor). **Empty for a non-AR manual** — the legacy loads the
     *  extra elements only for an AR manual, and a document step has nothing to show in a strip. */
    elements: z.array(ManualArElementSchema),
});
export const ManualBodySectionSchema = z.object({
    id: z.number().int(),
    order: z.number().int(),
    name: z.string(),
    steps: z.array(ManualBodyStepSchema),
});
/** The full body for the viewer: the requested manual's scalar context + viewer-affordance capabilities + the ORDERED
 *  outline of the CONTENT-OWNER (a linked child shares its parent's body — ADR-020). */
export const ManualBodySchema = z.object({
    manual: ManualSchema,
    /** The REQUESTED manual is archived (`archived_at IS NOT NULL`). The `Manual` DTO carries no archived flag by design (the
     *  lists are split by it); the reader needs it to gate affordances that make no sense on an archived manual — Print
     *  (ADR-031). A linked child reports ITS OWN archived state, not its parent's. */
    archived: z.boolean(),
    /** The facility ancestry (root → leaf) the manual sits under — the viewer's breadcrumb path (ADR-002 Phase 5). */
    facilityPath: z.array(z.object({ id: z.number().int(), name: z.string() })),
    capabilities: z.object({
        /** The caller created this manual → cannot rate it (legacy parity). */
        isCreator: z.boolean(),
        /** Published (`confirmed`/`marked`) AND not the creator → the rate-on-finish panel is offered (ADR-021). */
        canRate: z.boolean(),
        /** The caller has `manuals_manage` on the facility → the report/mark affordance is shown (ADR-018/002). */
        canReport: z.boolean(),
        /** The caller has the facility **`confirm`** tier — drives the editor's confirm-tier status transitions (Confirm /
         *  Unmark / reopen). Without it those are hidden (a `manuals_manage` caller would 403). */
        canConfirm: z.boolean(),
        /** The caller MAY edit this manual's body — ADR-018 amendment (legacy parity): any **non-archived, non-linked** state
         *  (draft OR published), authz = `confirm` OR `manuals_manage`+assigned. The editor warns before editing a published one. */
        canEdit: z.boolean(),
        /** The realm's placeable step types (`changeStepType` accepts only these — e.g. Takeda has no score types). Gates the
         *  editor's Type picker so an author never picks a type this realm's blueprints can't satisfy (which would 422). */
        availableStepTypes: z.array(ManualStepTypeSchema),
        /**
         * The BODY is an AR manual's — i.e. the CONTENT OWNER's `is_ar_manual`, which is not always `manual.isArManual`.
         *
         * A linked child serves its parent's body (ADR-020) and the ETL imports each manual's `is_ar_manual`
         * independently of the link, so a child flagged non-AR can carry an AR parent's steps and elements. Gating the UI
         * on the requested manual's own scalar therefore hides the element strip on a body that HAS elements — the same
         * defect the server's element read had, one layer up (diff-panel AR P2). Gate the Type picker and the AR block
         * on THIS, never on `manual.isArManual`.
         */
        isArBody: z.boolean(),
        /**
         * Where the CONTENT OWNER is usable (ADR-057), for the reader. A linked copy can't set its own, so its flags are the ones
         * frozen when it was linked; the reader goes by the original's.
         */
        platforms: z.object({ is2dWebManual: z.boolean(), is3dWebManual: z.boolean(), isVrManual: z.boolean(), isArManual: z.boolean() }),
        /**
         * The caller may pick NON-PLACEABLE blueprints in the `+` element picker.
         *
         * The legacy shows those to a superadmin only (`isPlaceable || isSuperAdmin`, `ARLMS_BlueprintSelection.js:25`),
         * behind a "nicht platzierbare anzeigen" switch. The server enforces the same rule on the write; this tells the UI
         * whether to offer the switch at all, so a normal author is not shown a toggle that can only ever 422.
         */
        canUseNonPlaceableElements: z.boolean(),
    }),
    sections: z.array(ManualBodySectionSchema),
    /** The legacy "Alle Schritte" set (ADR-028): the AR elements bound to NO section and NO step, which Unity shows on
     *  every step. Empty for a document manual. Membership is the server's `ALL_STEPS_ELEMENT_SQL` — the same predicate
     *  every all-steps write uses for containment. */
    allStepsElements: z.array(ManualArElementSchema),
});
/* ── Manual body AUTHORING request bodies (ADR-002 Phase 9, RED ZONE) ──────────────────────────────────────────────
 * Fine-grained aggregate commands over the outline. EVERY command carries `expectedVersion` — the `manual.version` the
 * client's loaded body was built against — as the optimistic-concurrency token (panel H4): the server CASes it and 409s
 * `MANUAL_BODY_STALE` on any overlapping edit, so two editors can't lost-update. Each command also re-asserts the manual
 * is editable (draft, not archived, not a linked child) in its CAS. All commands RETURN the whole updated `ManualBody`
 * (new version + full outline) so the client replaces its cache without a partial-tree merge. */
const BodyExpectedVersion = z.object({ expectedVersion: z.number().int().nonnegative() });
const BodyName = z.string().trim().min(1).max(255);
/** Rich-text HTML for a step state (sanitized server-side, per-locale — panel C1); bounded like the description. */
const BodyText = z.string().max(20000);
/** `?expectedVersion=` for the DELETE commands (a DELETE carries no body; mirrors deleteManual's `?expected=`). */
const BodyDeleteQuery = z.object({ expectedVersion: z.coerce.number().int().nonnegative() });
export const CreateSectionBodySchema = BodyExpectedVersion.extend({ name: BodyName });
export const RenameSectionBodySchema = BodyExpectedVersion.extend({ name: BodyName });
export const CreateStepBodySchema = BodyExpectedVersion.extend({ name: BodyName });
export const RenameStepBodySchema = BodyExpectedVersion.extend({ name: BodyName });
/** Reorder = the WHOLE ordered sibling id-list (a permutation of the current live set); the server verifies it's a
 *  permutation and applies positions 0..n-1, else 409 BODY_ORDER_STALE (a concurrent add/remove changed the set). */
export const ReorderBodySchema = BodyExpectedVersion.extend({ order: z.array(z.number().int().positive()).min(1) });
/** Each state text is nullable HTML: a string SETS it (sanitized, per-locale merge), `null` CLEARS it, omitted leaves it. */
export const EditStepTextBodySchema = BodyExpectedVersion.extend({
    standard: BodyText.nullish(),
    completed: BodyText.nullish(),
    failed: BodyText.nullish(),
});
/** Move a step to another section of the SAME manual + the target section's whole new step order (incl. the moved step). */
export const MoveStepBodySchema = BodyExpectedVersion.extend({
    toSectionId: z.number().int().positive(),
    order: z.array(z.number().int().positive()).min(1),
});
/** Change a step's TYPE (Phase 9b) — swaps the step's main element to the target type's blueprint (legacy `ChangeMainElement`);
 *  the standard text + geometry carry over, the old type's payload is dropped (author re-enters). 422 if the realm lacks the
 *  blueprint (e.g. question_with_score / show_score exist only in xr-area). */
export const ChangeStepTypeBodySchema = BodyExpectedVersion.extend({ type: ManualStepTypeSchema });
/**
 * Re-point a step's MAIN ELEMENT at a different blueprint — the legacy `Auswählen` picker (AR P3).
 *
 * Supersedes {@link ChangeStepTypeBodySchema} in the UI: the legacy has no type dropdown *because* picking the main
 * element blueprint IS the type change. A blueprint carries more than a type, though — takeda has 3854 steps on
 * `Hauptelement Text links` and 1771 on `MainElement_v2`, both of which derive `regular`. So this picks a LAYOUT, and
 * the step's `type` is derived from the chosen blueprint's name rather than sent by the client.
 */
export const SetStepMainElementBodySchema = BodyExpectedVersion.extend({ blueprintId: z.number().int().positive() });
/**
 * Add a non-main AR element to a step — the legacy `+` / `XR Element Auswahl` picker (AR P3d).
 *
 * `sensorId` names the PHYSICAL DEVICE for a Sensoren-category blueprint (ADR-038 D5), where the picker offers one
 * tile per device rather than one per blueprint. **Required** there and refused anywhere else: a sensor element with
 * no device is precisely the dangling row ADR-037 D4 has to special-case. The server re-derives the rest — it checks
 * the device is live, in this realm, and that its `type` equals the blueprint's `name`; nothing about that mapping is
 * trusted from the client.
 */
export const AddStepElementBodySchema = BodyExpectedVersion.extend({
    blueprintId: z.number().int().positive(),
    sensorId: z.number().int().positive().optional(),
});
/** Remove one of a step's non-main elements. The MAIN element is not removable — the step would have no content carrier. */
export const RemoveStepElementBodySchema = BodyExpectedVersion;
/** Write a typed step's interactive PAYLOAD (Phase 9b) — the discriminated union whose `kind` MUST match the step's current
 *  `type` (else 422); persisted to the main element's `Question`/`Checklist`/`QuestionWithScore`/`ScoreLevel` setting. */
export const EditStepPayloadBodySchema = BodyExpectedVersion.extend({ payload: StepPayloadSchema });
/**
 * One device in a manual's hand-speed rig (ADR-039).
 *
 * `isHandSpeed` is false for a device that is LINKED but is not a `HandSpeedSensor` — production holds exactly one
 * (xr-area's `X14`, a `SwitchSensor`). The legacy lists such a row while its picker cannot see it, so it can never be
 * taken off; here it is shown, marked, and removable (D4).
 */
export const HandspeedSensorSchema = z.object({
    id: z.number().int(),
    slug: z.string(),
    displayName: z.string().nullable(),
    description: z.string().nullable(),
    isHandSpeed: z.boolean(),
});
export const ManualHandspeedSchema = z.object({
    /** `manual.handspeed_check` — whether the rig is in USE. Separate from which devices it uses (D3). */
    enabled: z.boolean(),
    /** Only a SENSOR manual may have a rig at all, exactly as the legacy gates the switch (D6). */
    editable: z.boolean(),
    /** The devices currently bound, by slug. */
    sensors: z.array(HandspeedSensorSchema),
    /** Every `HandSpeedSensor` in the realm — what the picker offers. A realm with none says so rather than
     *  rendering an empty grid; xr-area has zero. */
    candidates: z.array(HandspeedSensorSchema),
});
/** The flag and the list travel together, as the legacy's single PATCH does (D5). */
export const SetManualHandspeedBodySchema = z.object({
    enabled: z.boolean(),
    sensorIds: z.array(z.number().int().positive()).max(100),
});
/**
 * Manual SETTINGS (ADR-040) — the legacy's manual-settings window, minus what the rewrite already has.
 *
 * Partial: every field is optional and only what is sent is written. That matters for the sensor half, which a
 * non-SuperAdmin may not send at all (D2), so renaming must not require carrying a flag the caller cannot set.
 * The CATEGORY is deliberately absent — `moveManual` already takes `{ facilityId, categoryId }`, which IS the
 * legacy's category dropdown (§1).
 */
export const SetManualSettingsBodySchema = z
    .object({
    name: z.string().trim().min(1).max(255),
    /** Rich-text HTML, sanitized on write like every other create/edit path (ADR-013 §3). */
    description: z.string().max(20000),
    is2dWebManual: z.boolean(),
    is3dWebManual: z.boolean(),
    isVrManual: z.boolean(),
    isArManual: z.boolean(),
    /** SuperAdmin-only (D2). Turning it OFF is refused while any sensor is still bound (D4) and clears
     *  `handspeed_check` (D3); turning it ON disturbs nothing else. */
    isSensorManual: z.boolean(),
})
    .partial()
    .refine((b) => Object.keys(b).length > 0, { message: "at least one field must be set" });
const UnauthorizedSchema = z.object({ message: z.string() });
const ManualForbidden = z.object({ message: z.string(), code: z.literal("MANUAL_FORBIDDEN") });
const ManualNotFound = z.object({ message: z.string(), code: z.literal("MANUAL_NOT_FOUND") });
/** The manual isn't in the published `{confirmed, marked}` set, so it can't be rated (ADR-021). */
const ManualNotRateable = z.object({ message: z.string(), code: z.literal("MANUAL_NOT_RATEABLE") });
/** The caller is the manual's creator — a creator cannot rate their own manual (ADR-021 / legacy parity). */
const RatingForbidden = z.object({ message: z.string(), code: z.literal("RATING_FORBIDDEN") });
/** No rating with that id under this manual (the parent-child scoping invariant — ADR-021 §4). */
const RatingNotFound = z.object({ message: z.string(), code: z.literal("RATING_NOT_FOUND") });
/** The check route resolves the parent manual FIRST (404 MANUAL_NOT_FOUND) then the rating under it (404
 *  RATING_NOT_FOUND), so its 404 body carries EITHER code (diff-panel #3 — the schema must admit both). */
const CheckRatingNotFound = z.union([ManualNotFound, RatingNotFound]);
/** A review-transition conflict (ADR-018): the manual's state changed since the client loaded it (`MANUAL_STATE_STALE`
 *  → the UI refreshes) or the requested `expectedFrom` isn't a legal source for this transition (`MANUAL_TRANSITION_INVALID`). */
const ManualTransitionConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_STATE_STALE", "MANUAL_TRANSITION_INVALID"]),
});
/** Every review transition carries the state the client BELIEVES the manual is in — the atomic compare-and-set guard
 *  (ADR-018 §3): the server only transitions when the actual state still matches, else 409 (no silent clobber). */
const TransitionBody = z.object({ expectedFrom: ManualStateSchema });
/* ── Manual body AUTHORING conflicts (ADR-002 Phase 9). NOT_EDITABLE = the manual isn't draft / is archived (reopen via
 * return-to-draft first); IS_LINK = it's a linked child (edit the SOURCE manual, not the link); BODY_STALE =
 * `expectedVersion` no longer matches (a concurrent edit won — refetch + retry); ORDER_STALE = the sibling set changed
 * under a reorder/move (refetch); LAST_SECTION = can't delete a manual's only section; NAME_CONFLICT = a live sibling
 * already carries the target name (names are the legacy element-binding key — a collision would merge two groups' refs). */
const BodyEditConflict = z.object({
    message: z.string(),
    // `MANUAL_LOCK_LOST` belongs here because `resolveEditableManual` — which EVERY body write goes through — calls
    // `assertNotLockedByAnother` and throws it when someone else holds a live edit lease (ADR-026). It was missing, so
    // the generated client's 409 union could not represent a response the server genuinely sends (diff-panel).
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "MANUAL_BODY_STALE", "MANUAL_LOCK_LOST"]),
});
const BodyNameConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "MANUAL_BODY_STALE", "BODY_NAME_CONFLICT"]),
});
const BodyOrderConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "MANUAL_BODY_STALE", "BODY_ORDER_STALE"]),
});
// moveStep can hit BOTH the permutation race AND a target-section name collision (the moved step's name already lives in
// the destination — names are the legacy binding key, guarded like create/rename; panel diff #2).
const BodyMoveConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "MANUAL_BODY_STALE", "BODY_ORDER_STALE", "BODY_NAME_CONFLICT"]),
});
const BodyDeleteSectionConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "MANUAL_BODY_STALE", "BODY_LAST_SECTION"]),
});
const SectionNotFoundSchema = z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "SECTION_NOT_FOUND"]) });
const StepNotFoundSchema = z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "STEP_NOT_FOUND"]) });
const MoveStepNotFoundSchema = z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "SECTION_NOT_FOUND", "STEP_NOT_FOUND"]) });
// Phase 9b: the realm has no placeable blueprint for the requested step type (question_with_score / show_score exist only in
// some realms) → the UI disables that type. And a payload whose `kind` doesn't match the step's current `type`.
const BlueprintUnavailable = z.object({ message: z.string(), code: z.literal("BLUEPRINT_UNAVAILABLE") });
const StepPayloadMismatch = z.object({ message: z.string(), code: z.literal("STEP_PAYLOAD_TYPE_MISMATCH") });
// attachStepMedia (ADR-024 Phase 11a-4): the picked library file (image/video) attached to a step's media. `expectedVersion`
// CAS like every body write; `fileId` is the library file (the media KIND is derived server-side — never trusted from the
// client, panel R2-3). 404 also covers a missing / non-ready / out-of-master-scope file (FILE_NOT_FOUND); 422 an unsupported
// media kind (only image/video attach today).
// `expectedSourceFileId` (ADR-025 addendum, panel H3): a DELAYED attach (the video-cut "ready → attach" hop) carries the
// step-media file id it believes is current as a SEMANTIC precondition — version-CAS alone can't guard it, because the web
// write-gate absorbs background version refetches while idle (useBodyWriteGate), so a stale delayed attach would pass CAS
// and silently clobber a newer concurrent media. Mismatch → 409 STEP_MEDIA_CHANGED (the cut stays in Master; the UI
// prompts instead of clobbering). Omitted (`undefined`) = the ordinary immediate picker attach, no precondition.
export const AttachStepMediaBodySchema = BodyExpectedVersion.extend({
    fileId: z.number().int().positive(),
    expectedSourceFileId: z.number().int().positive().optional(),
});
const AttachMediaNotFound = z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "STEP_NOT_FOUND", "FILE_NOT_FOUND"]) });
const MediaKindUnsupported = z.object({ message: z.string(), code: z.literal("MEDIA_KIND_UNSUPPORTED") });
// attach's 409 = the body-edit gate codes + the H3 semantic precondition miss.
const AttachMediaConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "MANUAL_BODY_STALE", "STEP_MEDIA_CHANGED"]),
});
/* ── Step video CUT (ADR-025 addendum, OWNER DECISION v2 / R1, RED ZONE) ───────────────────────────────────────────
 * `POST /manuals/:id/steps/:stepId/media/cut` — an ASYNC command; the WORKER cuts and the API LANDS the result (no
 * client attach round-trip). **R1 (2026-08-30, plan-panel-driven): every cut SAVES AS NEW** — the command allocates a
 * fresh pending Master `file`; the worker re-encodes the trim straight to it; the API landing arm re-points the step to
 * it and then GC's the OLD source iff it is now unreferenced (sole → old removed, feels in-place; shared → old kept,
 * original preserved). No in-place overwrite of the source's id-derived blob (the plan panel flagged that as a
 * non-atomic S3+DB corruption risk). ROW-FIRST outbox (the job row + the pending file commit together), best-effort
 * enqueue after commit, 202 `{jobId, targetFileId, targetName}`. NO body write / version bump here — the API landing arm
 * does that (so the command carries NO `expectedVersion`). Bounds are CHEAP (no ffprobe in the API — panel H5): the
 * worker probes the real duration and fails the job when the window overruns the source. The `name` is a LABEL only
 * (panel H2): the server strips any path/extension and canonicalizes to `<base>.mp4`. */
export const MAX_VIDEO_CUT_SECONDS = 3600; // 1h cap on the requested window (cheap sanity; the worker enforces reality)
export const CutStepVideoBodySchema = z.object({
    /** The video to cut — MVP: must be the step's CURRENTLY-ATTACHED video (panel C1 + ▶ #4), else 409 CUT_SOURCE_NOT_CURRENT. */
    sourceFileId: z.number().int().positive(),
    startSeconds: z.number().finite().min(0).max(86400),
    durationSeconds: z.number().finite().positive().max(MAX_VIDEO_CUT_SECONDS),
    /** Optional display label for the result; sanitized server-side (basename, no extension) → stored as `<base>.mp4`. */
    name: z.string().trim().max(255).optional(),
});
/** 202 — the cut is queued. `targetFileId` is the freshly-allocated pending Master file the worker will fill and the API
 *  landing arm will connect to the step. `jobId` feeds `getManualMediaJob` (plumbing for the later UI refresh). */
export const CutStepVideoAcceptedSchema = z.object({
    jobId: z.number().int().positive(),
    targetFileId: z.number().int().positive(),
    targetName: z.string(),
});
// Cut has NO version CAS (no body write) → no MANUAL_BODY_STALE; SOURCE_NOT_CURRENT = the step's attached video is no
// longer `sourceFileId` (someone swapped it — re-open the trim UI against the new video). (No SOURCE_BUSY under R1: two
// concurrent cuts just make two files, last-lands-wins with the loser GC'd — nothing is overwritten.)
const CutVideoConflict = z.object({
    message: z.string(),
    code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK", "CUT_SOURCE_NOT_CURRENT"]),
});
/** 429 — per-realm active-cut cap (pending|enqueued `video_cut` jobs); retry after one finishes. */
const CutQuotaExceeded = z.object({ message: z.string(), code: z.literal("CUT_QUOTA_EXCEEDED") });
/** 503 — degraded infra: the realm's `manuals` folder root isn't provisioned (the cut's `_cut` file lands in the manual's own
 *  folder now — ADR-024 #217, not Master), or the media queue isn't configured (round-2 R2-5 — REACHABLE from `cutStepVideo`,
 *  so a typed client must be able to model them). */
const CutServiceUnavailable = z.object({ message: z.string(), code: z.enum(["MANUALS_FOLDER_MISSING", "MEDIA_QUEUE_UNAVAILABLE"]) });
/* ── Media-job status (ADR-025 addendum, panel H4 — the poll contract the cut flow fires on) ─────────────────────
 * Tenant + manual-scoped: the job must carry this manual's id in its payload (cut jobs do), else 404 — a caller can
 * never read another manual's (or a payload-less thumbnail) job. `error` surfaces `last_error` ONLY in the terminal
 * `error` status; `target` is the pending/ready output file (null if the row vanished, e.g. reaped). */
export const ManualMediaJobSchema = z.object({
    jobId: z.number().int().positive(),
    kind: z.enum(["thumbnail", "video_cut", "collage"]),
    status: z.enum(["pending", "enqueued", "done", "error"]),
    error: z.string().nullable(),
    target: z
        .object({
        fileId: z.number().int().positive(),
        name: z.string(),
        status: z.enum(["pending", "ready", "error"]),
    })
        .nullable(),
});
const MediaJobNotFound = z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "MEDIA_JOB_NOT_FOUND"]) });
export const manualContract = c.router({
    listFacilityManuals: {
        method: "GET",
        path: "/facilities/:id/manuals",
        pathParams: z.object({ id: FacilityIdSchema }),
        query: z.object({
            // `"false"` (default) → ACTIVE manuals (`archived_at IS NULL`); `"true"` → ARCHIVED (`archived_at IS NOT NULL`).
            // Soft-deleted (`deleted_at`) rows are excluded either way. A STRING enum (not `z.coerce.boolean()`, whose
            // `Boolean("false")===true` footgun would make `?archived=false` archived) — typed end-to-end over the querystring.
            // Drives the manuals view's "Archived" filter chip.
            archived: z.enum(["true", "false"]).optional().default("false"),
        }),
        responses: {
            200: ManualListSchema,
            401: UnauthorizedSchema,
            // Gated by facility VISIBILITY (same derive as `getFacility`): manuals are content, so reading them requires
            // being able to view the facility — 403 if present-but-hidden, opaque 404 if absent in-tenant.
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "A facility's manuals with their category label (authz: view on the facility; active by default, `?archived=true` for archived).",
    },
    // ── Global manual search (FN4b-2, the legacy "Suche"). Permission-filtered server-side to the caller's DIRECTLY-
    // viewable facilities (the #107 predicate — no 403; a non-viewable manual is simply absent). Empty/short `q` is
    // rejected at the ts-rest boundary (min 1). `field=name` (default) matches the title; `field=description` matches the
    // tag-stripped body. Active only (manual + facility). Returns manual card fields + facility location + a text snippet.
    searchManuals: {
        method: "GET",
        path: "/manuals/search",
        query: z.object({
            q: z.string().trim().min(1).max(200),
            field: z.enum(["name", "description"]).optional().default("name"),
        }),
        responses: {
            200: ManualSearchListSchema,
            401: UnauthorizedSchema,
        },
        summary: "Search manuals by name or description across the caller's directly-viewable facilities (permission-filtered, active only).",
    },
    // ── Create (ADR-013, RED ZONE). Authz = flat `manuals_manage` on the facility (a create makes a DRAFT — like
    // duplicate; the state-dependent `confirm` gate only guards *confirmed* manuals). Resolves the target `facility_category`
    // (404 CATEGORY_NOT_ATTACHED). Takes the facility topology lock (it INSERTs). `description` is rich-text HTML, sanitized
    // server-side to the editor allowlist. Writes a `manual_audit` 'create' row.
    createManual: {
        method: "POST",
        path: "/facilities/:id/manuals",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({
            categoryId: FacilityIdSchema,
            name: z.string().trim().min(1).max(255),
            // Rich-text HTML (TipTap → the sanitizer allowlist). Bounded to keep an over-large body out of the DB; over-limit
            // is rejected at the ts-rest boundary (400 — the web prevents it + maps it; plan panel #3).
            description: z.string().max(20000).optional(),
            // Without the other three, the legacy's rule decides: AR means all four, otherwise 2D only.
            isArManual: z.boolean(),
            is2dWebManual: z.boolean().optional(),
            is3dWebManual: z.boolean().optional(),
            isVrManual: z.boolean().optional(),
        }),
        responses: {
            200: ManualSchema,
            // An over-limit body (name > 255 / description > 20000) is rejected at the ts-rest/Zod boundary BEFORE the
            // handler, with its native RequestValidationError shape — NOT a bespoke `code` (diff-panel #1). The web prevents
            // it (Name `maxLength` + a description length gate) and maps any 400 to inline copy on STATUS, not code. Declared
            // permissively so the contract doesn't advertise a code the runtime never emits.
            400: z.object({ message: z.string().optional() }).passthrough(),
            401: UnauthorizedSchema,
            403: ManualForbidden,
            // The category isn't attached to this facility (no `facility_category` to file the manual under). A hidden/absent
            // facility fails the authz gate first → 403 (fail-closed, opaque), mirroring moveManual's target handling.
            404: z.object({ message: z.string(), code: z.literal("CATEGORY_NOT_ATTACHED") }),
            // 503 — the realm's reserved `manuals` root folder isn't provisioned, so the per-manual project folder can't be
            // seeded (ADR-024 addendum #217). A persistent provisioning failure, not a transient retry — mirrors the sibling
            // cutStepVideo's typed 503 for MANUALS_FOLDER_MISSING (diff-panel P1a-F1).
            503: z.object({ message: z.string(), code: z.literal("MANUALS_FOLDER_MISSING") }),
            422: z.object({ message: z.string(), code: z.literal("MANUAL_PLATFORM_REQUIRED") }),
        },
        summary: "Create a draft manual in a facility's category (authz: `manuals_manage` on the facility).",
    },
    // ── Lifecycle mutations (ADR-012, RED ZONE). Authz is STATE-DEPENDENT on the manual's parent facility (a `confirmed`
    // manual needs `confirm`; else `manuals_manage`), except DUPLICATE (flat `manuals_manage` — it makes a draft) and MOVE
    // (both source AND target, state-dependent). Bypass tier bypasses. Membership mutations (duplicate/move/delete) take
    // the facility topology lock (ADR-010 §7b). Every mutation writes a `manual_audit` row.
    archiveManual: {
        method: "POST",
        path: "/manuals/:id/archive",
        pathParams: z.object({ id: ManualIdSchema }),
        // `expectedFrom` (optional): the state the client BELIEVES the manual is in — when present the compare-and-set matches
        // on it (409 MANUAL_STATE_STALE if the manual moved since it was rendered), exactly like the 5 review transitions.
        body: z.object({ expectedFrom: ManualStateSchema.optional() }),
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            // MANUAL_STATE_STALE: the row's state/category changed between the authz read and the compare-and-set (ADR-018 pattern).
            409: z.object({ message: z.string(), code: z.literal("MANUAL_STATE_STALE") }),
        },
        summary: "Archive a manual (soft-archive; authz: state-dependent on the parent facility).",
    },
    // The trash (ADR-056, RED ZONE). Trash and restore carry the state the client believes, like archive.
    listFacilityManualTrash: {
        method: "GET",
        path: "/facilities/:id/manuals/trash",
        pathParams: z.object({ id: FacilityIdSchema }),
        responses: {
            200: ManualTrashSchema,
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.enum(["FACILITY_FORBIDDEN", "MANUAL_FORBIDDEN"]) }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "A facility's trashed manuals, newest first, with the realm's retention (authz: direct view + manuals_manage).",
    },
    trashManual: {
        method: "POST",
        path: "/manuals/:id/trash",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({ expectedFrom: ManualStateSchema.optional() }),
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: z.object({ message: z.string(), code: z.literal("MANUAL_STATE_STALE") }),
        },
        summary: "Move a manual and its linked copies to the trash (idempotent; authz: state-dependent on the parent facility).",
    },
    restoreManual: {
        method: "POST",
        path: "/manuals/:id/restore",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({ expectedFrom: ManualStateSchema.optional() }),
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: z.object({ message: z.string(), code: z.enum(["MANUAL_STATE_STALE", "MANUAL_PARENT_TRASHED", "MANUAL_CATEGORY_GONE"]) }),
        },
        summary: "Restore a manual from the trash, with the copies trashed along with it (idempotent; authz: state-dependent).",
    },
    unarchiveManual: {
        method: "POST",
        path: "/manuals/:id/unarchive",
        pathParams: z.object({ id: ManualIdSchema }),
        // `expectedFrom` (optional): the state the client BELIEVES the manual is in — when present the compare-and-set matches
        // on it (409 MANUAL_STATE_STALE if the manual moved since it was rendered), exactly like the 5 review transitions.
        body: z.object({ expectedFrom: ManualStateSchema.optional() }),
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            // MANUAL_STATE_STALE: the row's state/category changed between the authz read and the compare-and-set (ADR-018 pattern).
            409: z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_ARCHIVED", "MANUAL_STATE_STALE", "MANUAL_PARENT_ARCHIVED"]) }),
        },
        summary: "Unarchive a manual (authz: state-dependent; the manual must be archived).",
    },
    duplicateManual: {
        method: "POST",
        path: "/manuals/:id/duplicate",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({}),
        responses: {
            // The new (draft) copy — same shape as a list row, so the client can drop it in / refetch.
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "Duplicate a manual as a draft copy (scalar copy; authz: `manuals_manage` on the parent facility).",
    },
    moveManual: {
        method: "POST",
        path: "/manuals/:id/move",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({ facilityId: FacilityIdSchema, categoryId: FacilityIdSchema }),
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            // MANUAL_NOT_FOUND (source absent in-tenant) OR MANUAL_MOVE_TARGET_INVALID (the category isn't attached to the
            // target facility, so there's no `facility_category` to move into).
            404: z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "MANUAL_MOVE_TARGET_INVALID"]) }),
        },
        summary: "Move a manual to another facility's category (authz: state-dependent on BOTH source + target facilities).",
    },
    // ── Linked manuals (ADR-020 addendum, RED ZONE). A linked CHILD mirrors a source manual's content but lives under
    // another facility/category (`parent_manual_id`). Create makes an auto-CONFIRMED child in the source's own
    // facility/category (the caller then MOVES it to the target via `moveManual` — legacy's 2-step flow). Unlink removes a
    // linked child (hard-delete; it's a 1-level leaf, no cascade). Both are `manuals_manage` on the relevant facility and
    // take the facility topology lock (they INSERT/DELETE a manual in a subtree). Every mutation writes a `manual_audit` row.
    createManualLink: {
        method: "POST",
        path: "/manuals/:id/links",
        pathParams: z.object({ id: ManualIdSchema }),
        // Create-and-place ATOMIC: the linked child is created directly in the chosen TARGET facility/category (no separate
        // move — a move of a `confirmed` manual would need `confirm`, which a `manuals_manage` link-creator lacks). `:id` is
        // the SOURCE manual.
        body: z.object({ facilityId: FacilityIdSchema, categoryId: FacilityIdSchema }),
        responses: {
            200: ManualSchema, // the new linked child (auto-confirmed, in the TARGET facility/category)
            401: UnauthorizedSchema,
            403: ManualForbidden,
            // MANUAL_NOT_FOUND (source absent in-tenant) OR CATEGORY_NOT_ATTACHED (the category isn't attached to the target
            // facility, so there's no `facility_category` to file the child under — mirrors createManual/moveManual).
            404: z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_FOUND", "CATEGORY_NOT_ATTACHED"]) }),
            // MANUAL_LINK_SOURCE_NOT_PUBLISHED (source still draft/ready_for_review — a link mirrors published content; legacy
            // gate "source must be confirmed"; `confirmed`/`marked` both = legacy `isConfirmed=true`) OR MANUAL_CANNOT_LINK_A_LINK
            // (the source is itself a linked child — chains aren't supported; would form a cascade-destructive grandchild).
            // MANUAL_LINK_SOURCE_ARCHIVED: a published-but-archived source (the archive cascade makes a child mirror its parent).
            409: z.object({ message: z.string(), code: z.enum(["MANUAL_LINK_SOURCE_NOT_PUBLISHED", "MANUAL_CANNOT_LINK_A_LINK", "MANUAL_LINK_SOURCE_ARCHIVED"]) }),
        },
        summary: "Create a linked copy of a published manual directly in a target facility/category (auto-confirmed; authz: `manuals_manage` on BOTH the source and target facilities).",
    },
    unlinkManual: {
        method: "POST",
        path: "/manuals/:id/unlink",
        pathParams: z.object({ id: ManualIdSchema }),
        body: z.object({}),
        responses: {
            200: z.object({ deleted: z.number().int() }),
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            // The manual isn't a linked child — only a link may be unlinked (never a source / standalone manual).
            409: z.object({ message: z.string(), code: z.literal("MANUAL_NOT_LINKED") }),
        },
        summary: "Remove a linked copy (hard-delete the linked child; authz: `manuals_manage` on its facility).",
    },
    manualStats: {
        method: "GET",
        path: "/manuals/:id/stats",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualStatsSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "Snapshot for the delete confirmation (authz: state-dependent on the parent facility).",
    },
    getManualAssignedUsers: {
        method: "GET",
        path: "/manuals/:id/assigned-users",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualAssignedUsersSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "The reviewers assigned to a manual (ADR-019 read-only; authz: DIRECT view on the parent facility).",
    },
    listElementBlueprints: {
        method: "GET",
        path: "/manuals/:id/element-blueprints",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ElementBlueprintCatalogSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            // The edit gate this route reuses throws 409 for a LINKED or ARCHIVED manual, exactly like every sibling command
            // behind `assertManualEditable`. Omitting it here would leave the generated client unable to narrow a real,
            // reachable outcome — it would land in the unexpected-error branch (diff-panel).
            409: z.object({ message: z.string(), code: z.enum(["MANUAL_NOT_EDITABLE", "MANUAL_IS_LINK"]) }),
        },
        summary: "The realm's AR-element blueprint catalogue for the element pickers (authz: the manual's own edit gate).",
    },
    getAssignableUsers: {
        method: "GET",
        path: "/manuals/:id/assignable-users",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualAssignedUsersSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden, // needs facility `confirm` (superadmin bypass)
            404: ManualNotFound,
        },
        summary: "Candidate reviewers for a manual (ADR-022; exactly-`manuals_manage` on the facility + prefix delegation; authz: `confirm`).",
    },
    assignManualUser: {
        method: "POST",
        path: "/manuals/:id/assign",
        pathParams: z.object({ id: ManualIdSchema }),
        body: ManualAssignBodySchema,
        responses: {
            200: ManualAssignedUsersSchema, // the updated assigned list, for the client to reconcile
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            // The posted user is not a valid candidate (server re-validates eligibility + delegation — panel Codex-#3).
            409: z.object({ message: z.string(), code: z.literal("MANUAL_ASSIGNEE_INELIGIBLE") }),
        },
        summary: "Assign a reviewer to a manual (ADR-022; authz: `confirm`; re-validates candidate eligibility server-side; audited).",
    },
    unassignManualUser: {
        method: "POST",
        path: "/manuals/:id/unassign",
        pathParams: z.object({ id: ManualIdSchema }),
        body: ManualAssignBodySchema,
        responses: {
            200: ManualAssignedUsersSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "Unassign a reviewer from a manual (ADR-022; authz: `confirm`; idempotent; audited).",
    },
    getManualHistory: {
        method: "GET",
        path: "/manuals/:id/history",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualHistorySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "The manual's audit timeline, newest-first (ADR-015 Phase B; authz: DIRECT view; never ships actor_sub/roles).",
    },
    getManualRatings: {
        method: "GET",
        path: "/manuals/:id/ratings",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualRatingsSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "Ratings for a manual: average + count + own + the identified list (ADR-021; authz: DIRECT view on the parent facility).",
    },
    getManualBody: {
        method: "GET",
        path: "/manuals/:id/body",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "The manual's ordered body (sections→typed steps) for the viewer (ADR-002; authz: DIRECT view + #173 state visibility; a linked child returns its parent's body).",
    },
    setManualSettings: {
        method: "PATCH",
        path: "/manuals/:id/settings",
        pathParams: z.object({ id: ManualIdSchema }),
        body: SetManualSettingsBodySchema,
        responses: {
            200: ManualSchema,
            // Over-limit name/description are rejected at the ts-rest/Zod boundary with its native shape, like createManual.
            400: z.object({ message: z.string().optional() }).passthrough(),
            401: UnauthorizedSchema,
            // MANUAL_FORBIDDEN: not an editor of this manual. SENSORS_FORBIDDEN: an editor who is not a SuperAdmin sent
            // `isSensorManual` (D2) — a distinct code so the UI can hide the switch rather than show a generic failure.
            403: z.object({ message: z.string(), code: z.enum(["MANUAL_FORBIDDEN", "SENSORS_FORBIDDEN"]) }),
            404: ManualNotFound,
            // `BodyEditConflict`, because this goes through `resolveEditableManual` like every body write: archived and
            // linked are refused there, the edit LEASE is refused there (`MANUAL_LOCK_LOST` — someone else is editing), and
            // the facility fence raises the fourth (D6). Declaring a subset made the client unable to represent a response
            // the server genuinely sends — the same omission that schema's own comment already records (diff panel).
            409: BodyEditConflict,
            // `isSensorManual: false` while sensors are still bound (D4). `sensors` names what is still attached, so the UI
            // can say WHICH rather than "something".
            422: z.union([
                z.object({
                    message: z.string(),
                    code: z.literal("SENSORS_STILL_BOUND"),
                    sensors: z.array(z.object({ id: z.number().int(), slug: z.string(), viaManualLink: z.boolean(), viaElement: z.boolean() })),
                }),
                z.object({ message: z.string(), code: z.literal("MANUAL_PLATFORM_REQUIRED") }),
            ]),
        },
        summary: "Set a manual's own settings — name, description, isArManual, isSensorManual (ADR-040).",
    },
    getManualHandspeed: {
        method: "GET",
        path: "/manuals/:id/handspeed",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: { 200: ManualHandspeedSchema, 401: UnauthorizedSchema, 403: ManualForbidden, 404: ManualNotFound },
        summary: "The manual's hand-speed rig: the flag, its bound devices, and the devices it could bind (ADR-039).",
    },
    setManualHandspeed: {
        method: "PATCH",
        path: "/manuals/:id/handspeed",
        pathParams: z.object({ id: ManualIdSchema }),
        body: SetManualHandspeedBodySchema,
        responses: {
            200: ManualHandspeedSchema,
            400: z.object({ message: z.string().optional() }).passthrough(),
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            // `BodyEditConflict` — same reasoning as setManualSettings. This shipped in ADR-039 declaring only three of the
            // four codes `resolveEditableManual` can raise; `MANUAL_LOCK_LOST` was missing (diff panel).
            409: BodyEditConflict,
            422: z.object({ message: z.string(), code: z.literal("HANDSPEED_UNAVAILABLE") }),
        },
        summary: "Set the hand-speed flag and replace its device list in one write (ADR-039).",
    },
    getManualLinks: {
        method: "GET",
        path: "/manuals/:id/links",
        pathParams: z.object({ id: ManualIdSchema }),
        responses: {
            200: ManualLinksSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
        },
        summary: "Linked manuals (ADR-020): the parent + children as view-filtered cards (authz: DIRECT view on the parent facility).",
    },
    upsertManualRating: {
        method: "PUT",
        path: "/manuals/:id/rating",
        pathParams: z.object({ id: ManualIdSchema }),
        body: UpsertRatingSchema,
        responses: {
            200: ManualRatingsSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden, // not directly viewable
            404: ManualNotFound,
            409: ManualNotRateable, // manual not in the published {confirmed, marked} set
            422: RatingForbidden, // the creator cannot rate their own manual
        },
        summary: "Upsert MY rating for the manual's current version (ADR-021; guards: DIRECT view + not-creator + published state).",
    },
    checkManualRating: {
        method: "POST",
        path: "/manuals/:id/ratings/:ratingId/check",
        pathParams: z.object({ id: ManualIdSchema, ratingId: RatingIdSchema }),
        body: CheckRatingSchema,
        responses: {
            200: RatingSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden, // not facility confirm-tier
            404: CheckRatingNotFound, // MANUAL_NOT_FOUND (bad :id) OR RATING_NOT_FOUND (no such rating under it)
        },
        summary: "Set a rating's moderation 'seen' flag (ADR-021; authz: facility confirm-tier; scoped to the parent manual).",
    },
    deleteManual: {
        method: "DELETE",
        path: "/manuals/:id",
        pathParams: z.object({ id: ManualIdSchema }),
        query: z.object({ expected: z.string().min(1) }),
        responses: {
            200: z.object({ deleted: z.number().int() }),
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: z.object({ message: z.string(), code: z.literal("MANUAL_STATS_STALE") }),
        },
        summary: "Hard-delete a manual, live or trashed, after a stats confirmation (authz: state-dependent on the parent facility).",
    },
    // ── Review state machine (ADR-018, RED ZONE). Explicit named transitions (never a raw `state` PATCH). Each carries
    // `expectedFrom` (the atomic compare-and-set guard → 409 MANUAL_STATE_STALE if the state moved). Authz reconciled with
    // the legacy + ADR-012: `confirm` gates publish/unpublish (confirm/unmark, and reopen from confirmed); `manuals_manage`
    // gates submit/withdraw and `mark` (flagging a fault — a low floor, legacy allowed any viewer). `marked` leaves ONLY
    // via `unmark` (no marked→draft — else mark+reopen would un-publish a confirmed manual with just `manuals_manage`).
    submitReviewManual: {
        method: "POST",
        path: "/manuals/:id/submit-review",
        pathParams: z.object({ id: ManualIdSchema }),
        body: TransitionBody,
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: ManualTransitionConflict,
        },
        summary: "Submit a draft manual for review (draft→ready_for_review; authz: manuals_manage).",
    },
    confirmManual: {
        method: "POST",
        path: "/manuals/:id/confirm",
        pathParams: z.object({ id: ManualIdSchema }),
        body: TransitionBody,
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: ManualTransitionConflict,
        },
        summary: "Confirm (publish) a manual under review (ready_for_review→confirmed; authz: confirm).",
    },
    markManual: {
        method: "POST",
        path: "/manuals/:id/mark",
        pathParams: z.object({ id: ManualIdSchema }),
        /**
         * The report (ADR-041). `comment` is the free text — 1500, the legacy's cap; the rewrite's earlier 1000 had to
         * reserve room inside itself for a flattened location suffix, which no longer rides along.
         *
         * `sectionId` / `stepId` are OPTIONAL and are resolved SERVER-side against the manual's CONTENT OWNER (a linked
         * child reports against its parent's body, ADR-041 D2) — the client never sends names, so a report cannot carry
         * a location that does not exist. Both omitted means "the whole manual", stored as the legacy's "Alle".
         */
        body: TransitionBody.extend({
            comment: z.string().trim().min(1).max(1500),
            sectionId: z.number().int().positive().optional(),
            stepId: z.number().int().positive().optional(),
        }),
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: ManualTransitionConflict,
            // A `sectionId`/`stepId` that is not in this manual's body, or a step that is not under the given section.
            422: z.object({ message: z.string(), code: z.literal("MARK_LOCATION_INVALID") }),
        },
        summary: "Flag a confirmed manual as faulty with a report — free text plus an optional section/step (ADR-041; confirmed→marked; authz: manuals_manage).",
    },
    unmarkManual: {
        method: "POST",
        path: "/manuals/:id/unmark",
        pathParams: z.object({ id: ManualIdSchema }),
        body: TransitionBody,
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: ManualTransitionConflict,
        },
        summary: "Clear a manual's fault flag (marked→confirmed; authz: confirm).",
    },
    returnManualToDraft: {
        method: "POST",
        path: "/manuals/:id/return-to-draft",
        pathParams: z.object({ id: ManualIdSchema }),
        body: TransitionBody,
        responses: {
            200: ManualSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: ManualTransitionConflict,
        },
        summary: "Return a manual to draft — withdraw from review (manuals_manage) or reopen a confirmed manual (confirm).",
    },
    // ── Manual body AUTHORING (ADR-002 Phase 9a, RED ZONE). Fine-grained aggregate commands over the outline (sections +
    // steps: create/rename/reorder/move/delete + edit step text). Authz = `assertManualEditable` (the manual is draft +
    // not-archived + not-a-linked-child, then `confirm` OR `manuals_manage`+assigned — the submit-gate twin). Each carries
    // `expectedVersion` (optimistic CAS → 409 MANUAL_BODY_STALE) and returns the WHOLE updated body (new version). Reorder
    // sends the whole sibling permutation → 409 BODY_ORDER_STALE. Rename/move cascade the legacy Section/Step name-binding
    // settings (Unity/mobile shadow — panel C2); delete tombstones the descendant element sub-tree. One `edit_body` audit row
    // per command. (Type change + typed-payload authoring + media attach are Phase 9b/11.)
    createSection: {
        method: "POST",
        path: "/manuals/:id/sections",
        pathParams: z.object({ id: ManualIdSchema }),
        body: CreateSectionBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: BodyNameConflict,
        },
        summary: "Append a section to a manual's outline (authz: editable — draft + confirm/assigned-manage).",
    },
    renameSection: {
        method: "PATCH",
        path: "/manuals/:id/sections/:sectionId",
        pathParams: z.object({ id: ManualIdSchema, sectionId: ManualIdSchema }),
        body: RenameSectionBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: SectionNotFoundSchema,
            409: BodyNameConflict,
        },
        summary: "Rename a section (cascades the legacy Section name-binding element settings; 409 on a sibling-name collision).",
    },
    reorderSections: {
        method: "PUT",
        path: "/manuals/:id/sections/order",
        pathParams: z.object({ id: ManualIdSchema }),
        body: ReorderBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: ManualNotFound,
            409: BodyOrderConflict,
        },
        summary: "Reorder a manual's sections (send the whole ordered id list; 409 BODY_ORDER_STALE if the set changed).",
    },
    deleteSection: {
        method: "DELETE",
        path: "/manuals/:id/sections/:sectionId",
        pathParams: z.object({ id: ManualIdSchema, sectionId: ManualIdSchema }),
        query: BodyDeleteQuery,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: SectionNotFoundSchema,
            409: BodyDeleteSectionConflict,
        },
        summary: "Delete a section + cascade-tombstone its steps/elements (soft; 409 BODY_LAST_SECTION on the only section).",
    },
    createStep: {
        method: "POST",
        path: "/manuals/:id/sections/:sectionId/steps",
        pathParams: z.object({ id: ManualIdSchema, sectionId: ManualIdSchema }),
        body: CreateStepBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: SectionNotFoundSchema,
            409: BodyNameConflict,
        },
        summary: "Append a (regular) step + its main element to a section (retype later via changeStepType; authz: editable).",
    },
    renameStep: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/name",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: RenameStepBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyNameConflict,
        },
        summary: "Rename a step (cascades the legacy Step name-binding setting; 409 on a sibling-name collision).",
    },
    editStepText: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/text",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: EditStepTextBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
        },
        summary: "Edit a step's standard/completed/failed instruction text (sanitized + per-locale merge on write).",
    },
    reorderSteps: {
        method: "PUT",
        path: "/manuals/:id/sections/:sectionId/steps/order",
        pathParams: z.object({ id: ManualIdSchema, sectionId: ManualIdSchema }),
        body: ReorderBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: SectionNotFoundSchema,
            409: BodyOrderConflict,
        },
        summary: "Reorder a section's steps (send the whole ordered id list; 409 BODY_ORDER_STALE if the set changed).",
    },
    moveStep: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/section",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: MoveStepBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: MoveStepNotFoundSchema,
            409: BodyMoveConflict,
        },
        summary: "Move a step to another section + set the target's step order (cascades the Section name-binding; authz: editable).",
    },
    deleteStep: {
        method: "DELETE",
        path: "/manuals/:id/steps/:stepId",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        query: BodyDeleteQuery,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
        },
        summary: "Delete a step + cascade-tombstone its elements (soft; authz: editable).",
    },
    // ── Manual body AUTHORING — typed content (ADR-002 Phase 9b, RED ZONE). changeStepType swaps the step's main element to
    // the target type's blueprint (legacy ChangeMainElement: standard text + geometry carry over, the old payload is dropped);
    // editStepPayload writes the type-specific interactive payload. Same golden pattern (editable gate + expectedVersion CAS +
    // edit_body audit + whole-body return).
    addStepElement: {
        method: "POST",
        path: "/manuals/:id/steps/:stepId/elements",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: AddStepElementBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: BlueprintUnavailable,
        },
        summary: "Add an AR element to a step from a blueprint (the legacy `+`); authz: editable.",
    },
    setElementSetting: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/elements/:elementId/settings/:name",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema, elementId: ManualIdSchema, name: z.string().min(1).max(64) }),
        body: SetElementSettingBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            // 404 also covers a setting the element does NOT already have — the editor edits VALUES, it never creates one.
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: SettingUnwritable,
        },
        summary: "Set one AR element setting's value (ADR-027 Phase B); authz: editable. RED ZONE — Unity shadow.",
    },
    copyElementSettings: {
        method: "POST",
        path: "/manuals/:id/steps/:stepId/elements/:elementId/settings/copy-from/:sourceElementId",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema, elementId: ManualIdSchema, sourceElementId: ManualIdSchema }),
        body: z.object({ expectedVersion: z.number().int().nonnegative() }),
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: SettingUnwritable,
        },
        summary: "The legacy `Einstellungen kopieren/einfügen` — copy one element's settings onto another. RED ZONE.",
    },
    duplicateStepElement: {
        method: "POST",
        path: "/manuals/:id/steps/:stepId/elements/:elementId/duplicate",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema, elementId: ManualIdSchema }),
        body: BodyExpectedVersion,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: BlueprintUnavailable,
        },
        summary: "The legacy `Element duplizieren` — deep-copy a non-main element (settings, files, geometry) into the same step. RED ZONE.",
    },
    /** The caller's ASSIGNED manuals as ids — the "Assigned to me" filter (ADR-019 Phase 4, read half). Same shape and
     *  permission filter as the favourites ids list: directly-viewable manuals only, so an assignment on a facility the
     *  caller can no longer view does not leak its id. */
    listMyManualAssignments: {
        method: "GET",
        path: "/user/manual-assignments",
        responses: {
            200: z.object({ manuals: z.array(z.number().int()) }),
            401: UnauthorizedSchema,
        },
        summary: "The caller's assigned manuals as ids (directly-viewable only) — powers the 'Assigned to me' filter.",
    },
    // ── ADR-028: the manual-level ("Alle Schritte") twins of the five step element writes. Same bodies, same fences,
    //    same codes; containment is the all-steps membership predicate instead of a step. RED ZONE, as their twins. ──
    addManualElement: {
        method: "POST",
        path: "/manuals/:id/elements",
        pathParams: z.object({ id: ManualIdSchema }),
        body: AddStepElementBodySchema,
        responses: { 200: ManualBodySchema, 401: UnauthorizedSchema, 403: ManualForbidden, 404: StepNotFoundSchema, 409: BodyEditConflict, 422: BlueprintUnavailable },
        summary: "Add a blueprint clone to the manual's all-steps set (the legacy `AddNewARElementToAllSteps`). RED ZONE.",
    },
    removeManualElement: {
        method: "DELETE",
        path: "/manuals/:id/elements/:elementId",
        pathParams: z.object({ id: ManualIdSchema, elementId: ManualIdSchema }),
        query: BodyDeleteQuery,
        responses: { 200: ManualBodySchema, 401: UnauthorizedSchema, 403: ManualForbidden, 404: StepNotFoundSchema, 409: BodyEditConflict, 422: SettingUnwritable },
        summary: "Remove an all-steps element (inbound Path endpoints are repaired first). RED ZONE.",
    },
    duplicateManualElement: {
        method: "POST",
        path: "/manuals/:id/elements/:elementId/duplicate",
        pathParams: z.object({ id: ManualIdSchema, elementId: ManualIdSchema }),
        body: BodyExpectedVersion,
        responses: { 200: ManualBodySchema, 401: UnauthorizedSchema, 403: ManualForbidden, 404: StepNotFoundSchema, 409: BodyEditConflict, 422: BlueprintUnavailable },
        summary: "Duplicate an all-steps element in place. RED ZONE.",
    },
    setManualElementSetting: {
        method: "PATCH",
        path: "/manuals/:id/elements/:elementId/settings/:name",
        pathParams: z.object({ id: ManualIdSchema, elementId: ManualIdSchema, name: z.string().min(1).max(100) }),
        body: SetElementSettingBodySchema,
        responses: { 200: ManualBodySchema, 401: UnauthorizedSchema, 403: ManualForbidden, 404: StepNotFoundSchema, 409: BodyEditConflict, 422: SettingUnwritable },
        summary: "Set one setting on an all-steps element. RED ZONE.",
    },
    copyManualElementSettings: {
        method: "POST",
        path: "/manuals/:id/elements/:elementId/settings/copy-from/:sourceElementId",
        pathParams: z.object({ id: ManualIdSchema, elementId: ManualIdSchema, sourceElementId: ManualIdSchema }),
        body: z.object({ expectedVersion: z.number().int().nonnegative() }),
        responses: { 200: ManualBodySchema, 401: UnauthorizedSchema, 403: ManualForbidden, 404: StepNotFoundSchema, 409: BodyEditConflict, 422: SettingUnwritable },
        summary: "Paste the clipboard onto an all-steps element. RED ZONE.",
    },
    removeStepElement: {
        method: "DELETE",
        path: "/manuals/:id/steps/:stepId/elements/:elementId",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema, elementId: ManualIdSchema }),
        // `expectedVersion` in the QUERY, matching every sibling DELETE here (deleteStep, deleteSection,
        // hardDeleteManual). A DELETE body is routinely stripped by fetch stacks and the fronting gravitee gateway, so
        // the CAS token would arrive empty and the route would 400 the moment the UI called it (diff-panel).
        query: BodyDeleteQuery,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: BlueprintUnavailable,
        },
        summary: "Remove a step's non-main AR element (the main one is not removable); authz: editable.",
    }, /** Remove a step's media (the legacy `×` on the file tile — `SetSetting(..., [])`): tombstones the file rows on the
     *  main element's media settings; the text stays. A step with no media is a no-op (still CAS-fenced). ADR-027 §9. */
    detachStepMedia: {
        method: "DELETE",
        path: "/manuals/:id/steps/:stepId/media",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        query: BodyDeleteQuery,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
        },
        summary: "Remove a step's media (image/video) — the legacy tile's ×.",
    },
    setStepMainElement: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/main-element",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: SetStepMainElementBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: BlueprintUnavailable,
        },
        summary: "Re-point a step's main element at a MainElements blueprint (the legacy Auswählen); type is DERIVED; authz: editable.",
    },
    changeStepType: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/type",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: ChangeStepTypeBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: BlueprintUnavailable,
        },
        summary: "Change a step's type (swaps the main-element blueprint; standard text + geometry carry over; authz: editable).",
    },
    editStepPayload: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/payload",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: EditStepPayloadBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: StepNotFoundSchema,
            409: BodyEditConflict,
            422: StepPayloadMismatch,
        },
        summary: "Write a typed step's interactive payload (question/checklist/score); the payload kind must match the step type.",
    },
    attachStepMedia: {
        method: "PATCH",
        path: "/manuals/:id/steps/:stepId/media",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: AttachStepMediaBodySchema,
        responses: {
            200: ManualBodySchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: AttachMediaNotFound,
            409: AttachMediaConflict,
            422: MediaKindUnsupported,
        },
        summary: "Attach a Master-library file (image/video) to a step's media (ADR-024 Phase 11a-4).",
    },
    // ── Step video cut (ADR-025 addendum, RED ZONE): async trim → worker cuts → API lands it (save-as-new + connect + GC). ──
    cutStepVideo: {
        method: "POST",
        path: "/manuals/:id/steps/:stepId/media/cut",
        pathParams: z.object({ id: ManualIdSchema, stepId: ManualIdSchema }),
        body: CutStepVideoBodySchema,
        responses: {
            202: CutStepVideoAcceptedSchema,
            // Service-side defense-in-depth bounds rejection (CUT_BOUNDS_INVALID) rides the same permissive 400 as the
            // ts-rest/Zod parse failures (createManual's pattern).
            400: z.object({ message: z.string().optional() }).passthrough(),
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: AttachMediaNotFound,
            409: CutVideoConflict,
            422: MediaKindUnsupported,
            429: CutQuotaExceeded,
            503: CutServiceUnavailable,
        },
        summary: "Queue an async cut of the step's attached video (202 + jobId + targetFileId; the worker cuts, the API lands it — save-as-new + connect + GC the old source if unreferenced).",
    },
    getManualMediaJob: {
        method: "GET",
        path: "/manuals/:id/media/jobs/:jobId",
        pathParams: z.object({ id: ManualIdSchema, jobId: ManualIdSchema }),
        responses: {
            200: ManualMediaJobSchema,
            401: UnauthorizedSchema,
            403: ManualForbidden,
            404: MediaJobNotFound,
            409: BodyEditConflict,
        },
        summary: "Poll a manual-scoped media job (plumbing for the later UI refresh — panel H4).",
    },
});
//# sourceMappingURL=manual.contract.js.map